import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { CPM_REPORT_CONCLUSIONS } from '@/queries/cpm-report';
import type { CpmReportConclusionsMutation, CpmReportConclusionsMutationVariables } from '@/generated/cpm-report';
import { buildReportFacts, reportFactsFingerprint, REPORT_EVIDENCE_LABELS, type ReportEvidenceKey } from './facts';
import { reportTimestamp, type CampaignReport } from './model';
import { CampaignReportError } from './types';

export type ReportConclusionItem = { title: string; text: string; evidenceKeys: ReportEvidenceKey[] };
export type ReportAnalysis = { fingerprint: string; generatedAt: string; summary: string;
  highlights: ReportConclusionItem[]; recommendations: ReportConclusionItem[] };
type AnalysisClient = Pick<ApolloClient<NormalizedCacheObject>, 'mutate'>;

function ensureActive(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Report cancelled', 'AbortError');
}
function invalid(): never { throw new CampaignReportError('ai_failed'); }
function object(value: unknown, keys: string[]): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) invalid();
  const record = value as Record<string, unknown>;
  if (Object.keys(record).filter((key) => key !== '__typename').length !== keys.length
    || !keys.every((key) => Object.prototype.hasOwnProperty.call(record, key))) invalid();
  return record;
}
function prose(value: unknown, max: number): string {
  if (typeof value !== 'string' || /[\p{N}\p{Cc}\p{Cf}`*#_[\]<>]/u.test(value)) invalid();
  const result = value.trim();
  if (!result || result.length > max) invalid();
  return result;
}
function items(value: unknown, min: number, max: number): ReportConclusionItem[] {
  if (!Array.isArray(value) || value.length < min || value.length > max) invalid();
  return value.map((entry) => {
    const item = object(entry, ['title', 'text', 'evidenceKeys']);
    if (!Array.isArray(item.evidenceKeys) || item.evidenceKeys.length < 1 || item.evidenceKeys.length > 3
      || new Set(item.evidenceKeys).size !== item.evidenceKeys.length
      || item.evidenceKeys.some((key) => typeof key !== 'string' || !Object.prototype.hasOwnProperty.call(REPORT_EVIDENCE_LABELS, key))) invalid();
    return { title: prose(item.title, 60), text: prose(item.text, 280), evidenceKeys: [...item.evidenceKeys] as ReportEvidenceKey[] };
  });
}

/** Treat generated prose as untrusted even when GraphQL supplied typed fields. */
export function validateReportAnalysis(value: unknown, expectedFingerprint: string, reportAsOf: string, now: number): ReportAnalysis {
  const data = object(value, ['fingerprint', 'generatedAt', 'summary', 'highlights', 'recommendations']);
  if (typeof data.fingerprint !== 'string' || !/^[a-f0-9]{64}$/.test(data.fingerprint)) invalid();
  if (data.fingerprint !== expectedFingerprint) throw new CampaignReportError('inconsistent');
  if (typeof data.generatedAt !== 'string' || data.generatedAt.length <= 10) invalid();
  let generated: number;
  try { generated = reportTimestamp(data.generatedAt); } catch { invalid(); }
  // Successful results may be reused briefly by the backend for identical facts.
  if (generated > now + 30000 || generated < reportTimestamp(reportAsOf) - 10 * 60000) invalid();
  return { fingerprint: data.fingerprint, generatedAt: new Date(generated).toISOString(), summary: prose(data.summary, 480),
    highlights: items(data.highlights, 2, 3), recommendations: items(data.recommendations, 2, 2) };
}

const SAFE_CODES: Record<string, CampaignReportError['code']> = {
  cpm_report_invalid: 'invalid', cpm_report_inconsistent: 'inconsistent',
  cpm_report_ai_unavailable: 'ai_unavailable', cpm_report_ai_busy: 'ai_busy', cpm_report_ai_failed: 'ai_failed',
};
function safeFailure(error: unknown): CampaignReportError {
  if (error instanceof CampaignReportError) return error;
  const source = error && typeof error === 'object' ? error as Record<string, unknown> : {};
  const errors = [source, ...(Array.isArray(source.errors) ? source.errors : []),
    ...(Array.isArray(source.graphQLErrors) ? source.graphQLErrors : [])];
  for (const entry of errors) {
    // Strapi wraps http-errors as STRAPI_* and preserves the stable code in message.
    for (const code of [entry?.extensions?.code, entry?.message, entry?.extensions?.error?.message]) {
      if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(SAFE_CODES, code)) return new CampaignReportError(SAFE_CODES[code]);
    }
  }
  return new CampaignReportError('ai_failed');
}

export async function generateCampaignReportAnalysis(client: AnalysisClient, report: CampaignReport,
  signal: AbortSignal, now: () => number = Date.now): Promise<ReportAnalysis> {
  ensureActive(signal);
  try {
    const fingerprint = await reportFactsFingerprint(buildReportFacts(report));
    ensureActive(signal);
    const result = await client.mutate<CpmReportConclusionsMutation, CpmReportConclusionsMutationVariables>({
      mutation: CPM_REPORT_CONCLUSIONS,
      variables: { campaignId: report.campaign.id, reportAsOf: report.collectionFinishedAt, expectedFactsFingerprint: fingerprint },
      fetchPolicy: 'no-cache', errorPolicy: 'all', context: { fetchOptions: { signal } },
    });
    ensureActive(signal);
    const apolloError = 'error' in result ? result.error : null;
    if (result.errors?.length || apolloError) throw safeFailure(apolloError || result);
    return validateReportAnalysis(result.data?.adminGenerateCpmCampaignReportConclusions, fingerprint, report.collectionFinishedAt, now());
  } catch (error) {
    ensureActive(signal);
    throw safeFailure(error);
  }
}
